import { generateProductPath } from './productCanonical';

/**
 * AI Discovery Routing Utilities
 * 
 * Cross-market URL generation for products and brands returned by AI Discovery.
 * Products may come from different markets (Canada/Global), so each needs
 * the correct PDP route regardless of which page the user is on.
 */

/**
 * Generate the correct product detail URL based on the product's country.
 * Canadian products → /country/canada/products/{id}/name/{slug}
 * All other products → /global/products/{id}/name/{slug}
 * 
 * @param {Object} product - Product object from AI Discovery response
 * @returns {string} Product detail URL path
 */
export const getProductDetailUrl = (product) => {
  const productId = product.productID || product._id || product.productId || product.id;
  const productName = product.name || product.title || product.productName || '';
  const country = (product.country || '').toLowerCase();

  if (!productId) {
    console.warn('AI Discovery: Missing product ID for URL generation');
    return '#';
  }

  return generateProductPath(country === 'canada' ? 'canada' : 'global', productId, productName) || '#';
};

/**
 * Generate the correct brand detail URL based on the brand's country.
 * Canadian brands → /country/canada/brands/{brandId}/{slug}
 * Global brands → /global/brands/{brandId}/{slug}
 * 
 * @param {Object} brand - Brand object from AI Discovery response
 * @returns {string} Brand detail URL path
 */
export const getBrandDetailUrl = (brand, market) => {
  const brandId = brand._id || brand.brandId || brand.id;
  const brandName = brand.name || brand.brandName || '';
  // Use explicit market param if provided, otherwise fall back to brand's country
  const country = market || (brand.country || '').toLowerCase();

  if (!brandId) {
    console.warn('AI Discovery: Missing brand ID for URL generation');
    return '#';
  }

  const slug = brandName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  if (country === 'canada') {
    return slug
      ? `/country/canada/brands/${brandId}/${slug}`
      : `/country/canada/brands/${brandId}`;
  }

  return slug
    ? `/global/brands/${brandId}/${slug}`
    : `/global/brands/${brandId}`;
};

/**
 * Smart context prepending for follow-up queries.
 * 
 * When a user sends a short, refinement-like query (e.g., "under $20", "only vegan"),
 * prepend the previous query to give the single-turn backend enough context.
 * 
 * @param {string} newQuery - The user's new query
 * @param {string} previousQuery - The last query in the conversation
 * @returns {string} The contextual query to send to the API
 */
export const buildContextualQuery = (newQuery, previousQuery) => {
  if (!previousQuery || !newQuery) return newQuery;

  const trimmed = newQuery.trim();

  // Heuristic: if the new query is short and starts with a refinement pattern,
  // prepend the previous query for context.
  // Use word boundaries (\b) to avoid matching partial words
  // (e.g., "shower products" should NOT match "show", "indoor" should NOT match "in")
  const refinementPatterns = /^(under|over|below|above|show me|filter by|only|also|and |but |with |without|from |more|less|cheaper|more expensive|not |just the|how about|what about|same|similar to)\b/i;

  if (trimmed.length < 50 && refinementPatterns.test(trimmed)) {
    return `${previousQuery}, ${trimmed}`;
  }

  return trimmed;
};

/**
 * Determine the market context from the current URL path.
 * 
 * @param {string} pathname - window.location.pathname or useLocation().pathname
 * @returns {'canada' | 'global'} Market string to pass to the API
 */
export const getMarketFromPath = (pathname) => {
  if (pathname.startsWith('/country/canada')) return 'canada';
  if (pathname.startsWith('/global')) return 'global';
  return 'canada'; // default fallback
};

/**
 * Determine if a product is Canadian based on its country field.
 * 
 * @param {Object} product - Product object
 * @returns {boolean}
 */
export const isCanadianProduct = (product) => {
  return (product.country || '').toLowerCase() === 'canada';
};

/**
 * Generate a frontend response text from structured AI Discovery results.
 * Since the backend returns structured data (products, brands, intent) without
 * natural language, the frontend generates the conversational intro text.
 * 
 * @param {Object} results - AI Discovery response data
 * @param {string} query - The query that was sent
 * @returns {string} Natural language response text
 */
export const generateResponseText = (results, query) => {
  const { products = [], brands = [], intent = {} } = results;
  const count = products.length;

  if (count === 0 && brands.length === 0) {
    return `I couldn't find products matching "${query}". Try different terms or browse our featured collections.`;
  }

  // Build natural response from intent
  const parts = [];
  if (count > 0) parts.push(`I found ${count} product${count !== 1 ? 's' : ''}`);

  if (intent.categories?.length) {
    parts.push(`in ${intent.categories.join(', ')}`);
  }
  if (intent.values?.length) {
    parts.push(`that are ${intent.values.join(', ')}`);
  }
  if (intent.priceRange?.max) {
    parts.push(`under $${intent.priceRange.max}`);
  }
  if (intent.priceRange?.min) {
    parts.push(`over $${intent.priceRange.min}`);
  }
  if (intent.countries?.length) {
    const countryNames = intent.countries.map(c => {
      if (c === 'canada') return 'Canada 🇨🇦';
      if (c === 'usa') return 'the US 🇺🇸';
      return c;
    });
    parts.push(`from ${countryNames.join(' and ')}`);
  }

  let text = parts.join(' ') + '!';

  if (brands.length > 0) {
    text += ` Plus ${brands.length} brand${brands.length !== 1 ? 's' : ''} you might like.`;
  }

  return text;
};
